import { objectKeys } from "app/helpers/objects";

import type { BaseFilterDto, BaseFilterValueList, ValueToDtoCategory } from "../types/filter-dto";

type CategoryTransforms<TValue extends BaseFilterValueList, TDto extends BaseFilterDto> = Partial<{
    [K in keyof TDto]: (dto: NonNullable<TDto[K]>) => Partial<TValue>;
}>;

type ValueToDtoTransforms<TValue extends BaseFilterValueList, TDto extends BaseFilterDto> = Partial<{
    [K in keyof TDto]: ValueToDtoCategory<TValue, TDto, K>;
}>;

/**
 * Flattens a category-keyed DTO into a flat panel map. Categories listed in
 * `customCategoryTransforms` run through their transformer; all others are
 * spread directly.
 */
export const filterDtoToPanelValue = <TValue extends BaseFilterValueList, TDto extends BaseFilterDto>(
    dtoValue: Partial<TDto>,
    customCategoryTransforms?: CategoryTransforms<TValue, TDto>,
): Partial<TValue> =>
    objectKeys(dtoValue as Record<string, unknown>).reduce<Partial<TValue>>((acc, key) => {
        const value = (dtoValue as TDto)[key as keyof TDto];

        if (value == null) {
            return acc;
        }

        const transform = customCategoryTransforms?.[key as keyof TDto];

        const contribution = transform ? transform(value) : (value as unknown as Partial<TValue>);

        return { ...acc, ...contribution };
    }, {});

/**
 * Folds a flat panel map back into a category-keyed DTO using the supplied
 * key→category mapping. Categories listed in `customCategoryTransforms` claim
 * their keys via an intrinsic `belongsTo` predicate; the engine buckets those
 * keys in the single pass and hands each `assemble` only its own bucket. All
 * other keys route through `filtersKeyToApiCategory` (grouped) or spread directly.
 */
export const filterPanelValueToDto = <TValue extends BaseFilterValueList, TDto extends BaseFilterDto>(
    filterValueList: Partial<TValue>,
    filtersKeyToApiCategory: Partial<Record<keyof TValue, keyof TDto>>,
    customCategoryTransforms?: ValueToDtoTransforms<TValue, TDto>,
): Partial<TDto> => {
    const transformCategories = objectKeys(
        (customCategoryTransforms ?? {}) as Record<string, unknown>,
    ) as (keyof TDto)[];
    const buckets = new Map<keyof TDto, Partial<TValue>>();
    const dto: Partial<TDto> = {};

    // Each panel key routes to exactly one sink: a predicate bucket, a grouped routing category, or top-level.
    for (const filterKey of objectKeys(filterValueList as BaseFilterValueList)) {
        const key = filterKey as keyof TValue;
        const value = filterValueList[key];

        // Intrinsic-predicate categories take precedence over the routing map.
        const transformCategory = transformCategories.find((category) =>
            customCategoryTransforms?.[category]?.belongsTo(key),
        );

        if (transformCategory) {
            const bucket = buckets.get(transformCategory) ?? {};

            (bucket as Record<keyof TValue, unknown>)[key] = value;

            buckets.set(transformCategory, bucket);

            continue;
        }

        const apiCategory = filtersKeyToApiCategory[key];

        if (!apiCategory) {
            continue;
        }

        const existingCategory = (dto[apiCategory] as BaseFilterDto | undefined) ?? {};

        (existingCategory as Record<string, unknown>)[filterKey] = value;

        (dto as Record<keyof TDto, unknown>)[apiCategory] = existingCategory;
    }

    for (const category of transformCategories) {
        const bucket = buckets.get(category);
        const transform = customCategoryTransforms?.[category];

        if (bucket && transform) {
            const assembled = transform.assemble(bucket);
            if (assembled != null) {
                (dto as Record<keyof TDto, unknown>)[category] = assembled;
            }
        }
    }

    return dto;
};
